import React, { useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import styled from 'styled-components';
import { useAuth } from '../context/AuthContext';
import Button from '../components/Button/Button';
import Input from '../components/Input/Input';
import LoadingSpinner from '../components/LoadingSpinner/LoadingSpinner';

const SignInWrapper = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${props => props.theme.spacing.lg};
  background-color: ${props => props.theme.colors.background.secondary};
`;

const SignInCard = styled.div`
  width: 100%;
  max-width: 400px;
  background-color: ${props => props.theme.colors.background.primary};
  border-radius: ${props => props.theme.borderRadius.lg};
  padding: ${props => props.theme.spacing.xxl};
  box-shadow: ${props => props.theme.shadows.lg};
`;

const SignInHeader = styled.div`
  text-align: center;
  margin-bottom: ${props => props.theme.spacing.xxl};
`;

const SignInTitle = styled.h1`
  font-size: ${props => props.theme.fontSize.xxl};
  font-weight: ${props => props.theme.fontWeight.bold};
  color: ${props => props.theme.colors.text.primary};
  margin-bottom: ${props => props.theme.spacing.sm};
`;

const SignInSubtitle = styled.p`
  font-size: ${props => props.theme.fontSize.sm};
  color: ${props => props.theme.colors.text.secondary};
`;

const SignInForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing.lg};
`;

const ForgotPassword = styled(Link)`
  font-size: ${props => props.theme.fontSize.sm};
  color: ${props => props.theme.colors.primary};
  text-decoration: none;
  text-align: right;
  margin-top: -${props => props.theme.spacing.sm};

  &:hover {
    text-decoration: underline;
  }
`;

const SignInFooter = styled.div`
  text-align: center;
  margin-top: ${props => props.theme.spacing.xl};
  padding-top: ${props => props.theme.spacing.xl};
  border-top: 1px solid ${props => props.theme.colors.border.light};
`;

const SignUpLink = styled.p`
  font-size: ${props => props.theme.fontSize.sm};
  color: ${props => props.theme.colors.text.secondary};

  a {
    color: ${props => props.theme.colors.primary};
    text-decoration: none;
    font-weight: ${props => props.theme.fontWeight.medium};

    &:hover {
      text-decoration: underline;
    }
  }
`;

const ErrorMessage = styled.div`
  background-color: ${props => props.theme.colors.error}08;
  border: 1px solid ${props => props.theme.colors.error}20;
  color: ${props => props.theme.colors.error};
  padding: ${props => props.theme.spacing.md};
  border-radius: ${props => props.theme.borderRadius.md};
  font-size: ${props => props.theme.fontSize.sm};
  margin-bottom: ${props => props.theme.spacing.lg};
`;

const SignInPage = () => {
  const { login, isLoading, error, clearError, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  
  const from = location.state?.from?.pathname || '/dashboard';

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm();

  useEffect(() => {
    if (isAuthenticated) {
      navigate(from, { replace: true });
    }
  }, [isAuthenticated, navigate, from]);

  useEffect(() => {
    clearError();
  }, []);

  const onSubmit = async (data) => {
    try {
      await login(data);
      navigate(from, { replace: true });
    } catch (err) {
      // Error is handled by context
    }
  };

  return (
    <SignInWrapper>
      <SignInCard>
        <SignInHeader>
          <SignInTitle>Welcome Back</SignInTitle>
          <SignInSubtitle>Sign in to your account</SignInSubtitle>
        </SignInHeader>

        {error && <ErrorMessage>{error}</ErrorMessage>}

        <SignInForm onSubmit={handleSubmit(onSubmit)}>
          <Input
            label="Email"
            type="email"
            placeholder="Enter your email"
            error={errors.email?.message}
            {...register('email', {
              required: 'Email is required',
              pattern: {
                value: /^\S+@\S+$/i,
                message: 'Please enter a valid email',
              },
            })}
          />

          <Input
            label="Password"
            type="password"
            placeholder="Enter your password"
            error={errors.password?.message}
            {...register('password', {
              required: 'Password is required',
              minLength: {
                value: 6,
                message: 'Password must be at least 6 characters',
              },
            })}
          />

          {/* <ForgotPassword to="/forgot-password">
            Forgot your password?
          </ForgotPassword> */}

          <Button
            type="submit"
            fullWidth
            disabled={isLoading}
          >
            {isLoading ? <LoadingSpinner size="20px" showText={false} /> : 'Sign In'}
          </Button>
        </SignInForm>

        <SignInFooter>
          <SignUpLink>
            Don't have an account? <Link to="/register">Sign up</Link>
          </SignUpLink>
        </SignInFooter>
      </SignInCard>
    </SignInWrapper>
  );
};

export default SignInPage;